import Btn from '@/components/dataDisplay/Btn';
import DisplayOnBrowserMount from '@/components/rehydration/DisplayOnBrowserMount';
import { AMPLITUDE_FEEDBACK_SURVEY_STEPS } from '@/modules/core/amplitude/events';
import useAmplitude from '@/modules/core/amplitude/hooks/useAmplitude';
import { LogEvent } from '@/modules/core/amplitude/types/Amplitude';
import UniversalCookiesManager from '@/modules/core/cookiesManager/UniversalCookiesManager';
import { CustomerTheme } from '@/modules/core/data/types/CustomerTheme';
import { GenericObject } from '@/modules/core/data/types/GenericObject';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { StudentSolutionContext } from '@/modules/core/data/types/StudentSolutionContext';
import { ALERT_TYPES } from '@/modules/core/sentry/config';
import useUserSession, { UserSession } from '@/modules/core/userSession/useUserSession';
import { serializeStudentSolutionContext } from '@/modules/solutions/studentSolutionContext';
import { Amplitude } from '@amplitude/react-amplitude';
import {
  css,
  useTheme,
} from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Sentry from '@sentry/node';
import { Identify } from 'amplitude-js';
import classnames from 'classnames';
import map from 'lodash.map';
import size from 'lodash.size';
import React, {
  Fragment,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Col,
  Row,
} from 'reactstrap';
import FeedbackSurveyRatings from './FeedbackSurveyRatings';
import SendSurvey from './SendSurvey';

type Props = {
  solutions: StudentSolution[];
  context: StudentSolutionContext;
  simulationsCount: number;
  searchFilter: string | null;
}

const CLOSE_AFTER_SUBMIT_TIMEOUT = 3000; // ms

/**
 * Display a feedback survey to the end-user.
 *
 * Meant to help gather analytic statistics about how much our users like the product.
 * Stores the grade/comment in cookies.
 * Displays the last selected smiley upon browser rehydration.
 *
 * Workflow:
 *  1. ASK_GRADE - Display a list of clickable smileys (AKA "grade")
 *  2. ASK_COMMENT - Once a smiley has been clicked, displays a comment input
 *  3. THANK_YOU - Once the submit button has been clicked, displays a "thank you" message and close the survey
 */
const FeedbackSurvey: React.FunctionComponent<Props> = (props) => {
  const {
    solutions,
    context,
    simulationsCount,
    searchFilter,
  } = props;
  const MIN_REQUIRED_COMMENT_LENGTH = 15;  // Min 15 chars, only when the comment is required (when the grade is too low)
  const { primaryColor }: CustomerTheme = useTheme();
  const { amplitudeInstance } = useAmplitude();
  const { t } = useTranslation();
  const userSession: UserSession = useUserSession();
  const universalCookiesManager = new UniversalCookiesManager(null, null, userSession); // Must provide readonlyCookies for SSR to work properly
  const cookieChatbotFeedback = userSession?.chatbotFeedback || null;
  const previousSessionGrade = cookieChatbotFeedback?.grade;
  const previousSessionComment = cookieChatbotFeedback?.comment;

  const [step, setStep] = useState<AMPLITUDE_FEEDBACK_SURVEY_STEPS>(AMPLITUDE_FEEDBACK_SURVEY_STEPS.ASK_GRADE);
  const [currentSessionGrade, setCurrentSessionGrade] = useState<number>(null);
  const [currentSessionComment, setCurrentSessionComment] = useState<string>(null);
  const [isClosed, setIsClosed] = useState<boolean>(false);
  const isCommentRequired = !currentSessionGrade || currentSessionGrade <= 3;
  const isSubmitButtonDisabled = isCommentRequired && ((currentSessionComment || previousSessionComment) || '').trim().length < MIN_REQUIRED_COMMENT_LENGTH;

  if (isClosed) {
    return null;
  }

  return (
    <Amplitude
      eventProperties={(inheritedProps): GenericObject => ({
        ...inheritedProps,
        solutions: {
          count: size(solutions),
          ref: map(solutions, 'ref'),
        },
        simulation: {
          filters: serializeStudentSolutionContext(context),
          searchFilter: searchFilter || null,
          counter: simulationsCount,
        },
      })}
    >
      {({ logEvent }: { logEvent: LogEvent }): JSX.Element => (
        <div
          css={css`
            .survey-container {
              background-color: white;
              margin-left: 45px;
              margin-right: 45px;
              margin-top: 15px;
              margin-bottom: 30px;
              padding: 30px;
              border-radius: 20px;
              text-align: center;
              justify-content: center;

              @media (max-width: 991.98px) {
                margin-left: 10px;
                margin-right: 10px;
                padding: 20px;
              }
            }

            .textarea {
              width: 40%;
              height: 100px;
              border: ${primaryColor} solid 2px;
              border-radius: 5px;

              @media (max-width: 991.98px) {
                width: 100%;
              }
            }

            .navbar-survey {
              display: inline-flex;
              place-content: space-between;
              width: 100%;
            }

            .btn-close-survey {
              position: absolute;
              top: 0;
              right: 0;

              svg {
                margin-top: 3px;
              }
            }

            .btn-send-survey {
              margin-top: 20px;
            }
          `}
        >
          <div className={classnames('survey-container')}>
            {
              (step === AMPLITUDE_FEEDBACK_SURVEY_STEPS.ASK_GRADE || step === AMPLITUDE_FEEDBACK_SURVEY_STEPS.ASK_COMMENT) && (
                <div>
                  <Row
                    style={{ margin: 0 }}
                  >
                    <Col>
                      <h2
                        style={{ marginTop: '40px' }}
                      >
                        {t('solutionsPage.feedbackSurvey.grade.question', `Trouvez-vous ces solutions utiles ?`)}
                      </h2>

                      {
                        (step === AMPLITUDE_FEEDBACK_SURVEY_STEPS.ASK_COMMENT || previousSessionGrade) && (
                          <Btn
                            mode={'primary-outline'}
                            className="btn-close-survey"
                            onClick={(): void => {
                              // If the user has selected any grade, that means he made a choice and we want to thank him
                              if (currentSessionGrade) {
                                setStep(AMPLITUDE_FEEDBACK_SURVEY_STEPS.THANK_YOU);

                                // Auto close
                                setTimeout(() => {
                                  setIsClosed(true);
                                }, CLOSE_AFTER_SUBMIT_TIMEOUT);
                              } else {
                                // Otherwise, he had made a choice previously and just wants to close the popup without updating his grade/comment
                                setIsClosed(true);
                              }
                            }}
                          >
                            <FontAwesomeIcon icon="times" />
                            <span className={'hide-sm-md'}>
                              {t('solutionsPage.feedbackSurvey.close', `Fermer`)}
                            </span>
                          </Btn>
                        )
                      }
                    </Col>
                  </Row>

                  <Row>
                    <Col>
                      {/*
                        XXX Because the grade is stored in cookie, and the cookie aren't readable by the server (because we use SSG)
                         then if we render this component by the server, the grade will be "undefined".
                         To display our component properly, we must wait for the browser rehydration to happen,
                         because the "grade" stored in the browser cookies will be readable then.
                      */}
                      <DisplayOnBrowserMount>
                        <FeedbackSurveyRatings
                          universalCookiesManager={universalCookiesManager}
                          previousSessionChatbotFeedback={cookieChatbotFeedback}
                          currentSessionGrade={currentSessionGrade}
                          setStep={setStep}
                          setCurrentGrade={setCurrentSessionGrade}
                        />
                      </DisplayOnBrowserMount>
                    </Col>
                  </Row>

                  {
                    step === AMPLITUDE_FEEDBACK_SURVEY_STEPS.ASK_COMMENT && (
                      <Fragment>
                        <Row
                          style={{
                            marginTop: 30,
                          }}
                        >
                          <Col>
                            {
                              size(previousSessionComment) ? (
                                <h3
                                  style={{ marginTop: '5px' }}
                                >
                                  {t(`solutionsPage.feedbackSurvey.comment.update`, `Souhaitez vous modifier votre commentaire ?`)}
                                </h3>
                              ) : (
                                <h3
                                  style={{ marginTop: '5px' }}
                                >
                                  {
                                    isCommentRequired ?
                                      t(`solutionsPage.feedbackSurvey.comment.add-required`, `Pour nous aider à nous améliorer, merci de préciser en commentaire`) :
                                      t(`solutionsPage.feedbackSurvey.comment.add-optional`, `Souhaitez vous ajouter un commentaire ? (optionnel)`)
                                  }
                                </h3>
                              )
                            }
                          </Col>
                        </Row>

                        <Row>
                          <Col>
                            <textarea
                              className="textarea"
                              placeholder={t('solutionsPage.feedbackSurvey.comment.placeholder', `Nous avons hâte de connaître votre avis !`)}
                              onChange={(event): void => {
                                setCurrentSessionComment(event?.target?.value);
                              }}
                              value={currentSessionComment || previousSessionComment || ''}
                            />
                          </Col>
                        </Row>

                        <Row>
                          <Col>
                            <Btn
                              mode={'primary-outline'}
                              disabled={isSubmitButtonDisabled}
                              className="btn-send-survey"
                              onClick={(): void => {
                                // Store comment in cookie
                                universalCookiesManager.patchUserData({
                                  chatbotFeedback: {
                                    ...cookieChatbotFeedback,
                                    grade: currentSessionGrade,
                                    comment: currentSessionComment,
                                  },
                                });

                                // XXX Historically, we used to send the grade as soon as it was selected, and then the comment
                                //  This led to analytics issues, because customers ended up with bad grades with no comment
                                //  We changed this behavior to only store the feedback (grade + ~comment) upon submit (comment is optional for high grades)
                                //  So, we still send those two information separately today to avoid breaking stuff in our analytics
                                // DA Store grade in analytics (old way)
                                logEvent('solutions-user-feedback-grade', {
                                  grade: currentSessionGrade,
                                });
                                // DA Store comment in analytics (old way)
                                logEvent('solutions-user-feedback-comment', {
                                  comment: currentSessionComment,
                                });
                                // DA Store grade + comment in analytics (new way - May 2022)
                                logEvent('solutions-user-feedback', {
                                  grade: currentSessionGrade,
                                  comment: currentSessionComment,
                                });

                                // DA Enhance user properties
                                const visitor: Identify = new amplitudeInstance.Identify();
                                visitor.setOnce('initial_solutions_feedback_comment', currentSessionComment); // DA This will only be applied once, the first time the user gives a comment
                                visitor.set('solutions_feedback_comment', currentSessionComment);
                                amplitudeInstance.identify(visitor);

                                // CS Send a notification to Sentry for our customer success team to be notified in real-time
                                Sentry.withScope((scope): void => {
                                  scope.setTag('alertType', ALERT_TYPES.USER_SEND_FEEDBACK_SURVEY);

                                  Sentry.captureEvent({
                                    message: `New user feedback survey comment "${currentSessionComment}" with grade ${currentSessionGrade}.`,
                                    level: Sentry.Severity.Log,
                                  });
                                });

                                setStep(AMPLITUDE_FEEDBACK_SURVEY_STEPS.THANK_YOU);

                                // Auto close
                                setTimeout(() => {
                                  setIsClosed(true);
                                }, CLOSE_AFTER_SUBMIT_TIMEOUT);
                              }}
                            >
                              <FontAwesomeIcon icon="paper-plane" />
                              {t('solutionsPage.feedbackSurvey.send', `Envoyer`)}
                            </Btn>
                          </Col>
                        </Row>
                      </Fragment>
                    )
                  }
                </div>
              )
            }

            {
              step === AMPLITUDE_FEEDBACK_SURVEY_STEPS.THANK_YOU && (
                <Fragment>
                  <h2
                    style={{ marginTop: '5px' }}
                  >
                    {t('solutionsPage.feedbackSurvey.thankYou', `Merci pour votre avis`)}
                  </h2>
                  <SendSurvey />
                </Fragment>
              )
            }
          </div>
        </div>
      )}
    </Amplitude>
  );
};

export default FeedbackSurvey;
